import { useContext, useEffect } from "react";

import { EditorContext } from "../containers/editor/EditorLayout";
import {
  getCssTransformObj,
  getCssTransformObjToString,
  getImgDimensionFromUrl,
  getRandomString,
  getZoomedValue,
} from "../_helpers/utils";
import {
  FRAME,
  GENERIC_EDITOR_WRAP_PADDING,
  PICTURE,
  STOCK,
  TYPE_INFOGRAPHIC,
  TYPE_PROPOSAL,
  VIDEO,
  VIDEO_DEFAULT_TRIM_START_TIME,
  WIDGET_HANDLER_MARGIN_TOP,
} from "../constants/editor";
import { widgetConfig } from "../components/Editor/editor_config";

const useClippingPicture = () => {
  let {
    metadata,
    updateMetadata,
    widgets,
    updateWidgets,
    dimension,
    pageNodes,
    isFrameContentActive,
    setIsFrameContentActive,
    isTimeLineViewOpen,
    documentType,
  } = useContext(EditorContext);

  const getNode = targetId => {
    let node;
    if (document.querySelector(`#${targetId} svg defs`) && isFrameContentActive) {
      node = document.querySelector(`#${targetId} svg`);
      node = node.children[1];
    } else {
      if (document.querySelector(`#${targetId} svg rect`)) {
        node = document.querySelector(`#${targetId} svg rect`);
      } else if (document.querySelector(`#${targetId} svg circle`)) {
        node = document.querySelector(`#${targetId} svg circle`);
      } else if (document.querySelector(`#${targetId} svg polygon`)) {
        node = document.querySelector(`#${targetId} svg polygon`);
      } else if (document.querySelector(`#${targetId} svg ellipse`)) {
        node = document.querySelector(`#${targetId} svg ellipse`);
      } else if (document.querySelector(`#${targetId} svg path`)) {
        node = document.querySelector(`#${targetId} svg path`);
      }
    }

    return node;
  };

  const clippingPicture = (activeImageSrc, targetId, insertType) => {
    if (insertType === "Replace") document.querySelector(`#${targetId} svg image`).setAttribute("href", activeImageSrc);
    else {
      let widget = document.getElementById(targetId);
      let pathId = getRandomString(8);
      let insidePathId = `inside_${getRandomString(8)}`;
      let node = getNode(targetId);
      node.setAttribute("id", pathId);

      const SVG_NS = "http://www.w3.org/2000/svg";
      const XLINK_NS = "http://www.w3.org/1999/xlink";

      // --- defs ---
      const defs = document.createElementNS(SVG_NS, "defs");

      // Create clipPath
      const clipPath = document.createElementNS(SVG_NS, "clipPath");
      clipPath.setAttribute("id", insidePathId);

      const use = document.createElementNS(SVG_NS, "use");
      use.setAttributeNS(XLINK_NS, "xlink:href", `#${pathId}`);
      clipPath.appendChild(use);

      defs.appendChild(clipPath);

      // Create image element
      const image = document.createElementNS(SVG_NS, "image");
      // cross-browser href
      image.setAttributeNS(null, "href", activeImageSrc);
      image.setAttributeNS(XLINK_NS, "xlink:href", activeImageSrc);

      image.setAttribute("x", "0");
      image.setAttribute("y", "0");
      image.setAttribute("width", "512");
      image.setAttribute("height", "512");
      image.setAttribute("clip-path", `url(#${insidePathId})`);
      image.setAttribute("preserveAspectRatio", "xMidYMid slice");

      defs.innerHTML = `${node.outerHTML}`;
      defs.appendChild(clipPath);
      defs.appendChild(image);

      // === Outline shape if border is applied===
      if (widget.getAttribute("data-border-style") && widget.getAttribute("data-border-style") === "solid") {
        const outline = document.createElementNS(SVG_NS, "use");
        outline.setAttributeNS(XLINK_NS, "xlink:href", `#${pathId}`);
        outline.setAttribute("fill", "none");
        outline.setAttribute("stroke", widget.getAttribute("data-border-color"));
        outline.setAttribute("stroke-width", widget.getAttribute("data-border-width"));
        outline.setAttribute("clip-path", `url(#${insidePathId})`);

        defs.appendChild(outline);
        widget.querySelector(".svg-border-overlay").remove(); // remove border div applied in video
      }

      widget.querySelector(".clip-wrapper-outer").remove();
      document.querySelector(`#${targetId} svg`).innerHTML = defs.innerHTML;
    }
  };

  // Utility: create a polygon string from an SVG node. (Supports <path>, <polygon>, <polyline>, <rect>, <circle>, <ellipse>.)
  function makePolygonFromNode(shapeNode, svgRoot, maxPoints = 160) {
    const SVG_NS = "http://www.w3.org/2000/svg";

    // Get viewBox (fallback to bbox if missing)
    let vb = svgRoot.getAttribute("viewBox");
    let vbX = 0,
      vbY = 0,
      vbW = 0,
      vbH = 0;
    if (vb) {
      [vbX, vbY, vbW, vbH] = vb.split(/\s+/).map(Number);
    } else {
      const bb = svgRoot.getBBox?.() || { x: 0, y: 0, width: 512, height: 512 };
      vbW = bb.width || 512;
      vbH = bb.height || 512;
    }
    if (!vbW || !vbH) {
      vbW = 512;
      vbH = 512;
    }

    // Ensure we operate on a path-like thing we can sample
    const tag = shapeNode.tagName.toLowerCase();
    let pathEl;

    if (tag === "path") {
      pathEl = shapeNode.cloneNode(true);
    } else if (tag === "polygon" || tag === "polyline") {
      const pts = (shapeNode.getAttribute("points") || "").trim();
      // Normalize points to percentages from viewBox units
      const pairs = pts.split(/[\s,]+/).map(Number);
      let coords = [];
      for (let i = 0; i < pairs.length; i += 2) {
        const x = ((pairs[i] - vbX) / vbW) * 100;
        const y = ((pairs[i + 1] - vbY) / vbH) * 100;
        coords.push(`${x}% ${y}%`);
      }
      // Close if polyline
      if (tag === "polyline" && coords.length) coords.push(coords[0]);
      return `polygon(${coords.join(",")})`;
    } else if (tag === "rect") {
      const x = +shapeNode.getAttribute("x") || 0;
      const y = +shapeNode.getAttribute("y") || 0;
      const w = +shapeNode.getAttribute("width") || 0;
      const h = +shapeNode.getAttribute("height") || 0;
      const rx = +shapeNode.getAttribute("rx") || 0;
      const ry = +shapeNode.getAttribute("ry") || 0;

      if (!rx && !ry) {
        const p = [
          [((x - vbX) / vbW) * 100, ((y - vbY) / vbH) * 100],
          [((x + w - vbX) / vbW) * 100, ((y - vbY) / vbH) * 100],
          [((x + w - vbX) / vbW) * 100, ((y + h - vbY) / vbH) * 100],
          [((x - vbX) / vbW) * 100, ((y + h - vbY) / vbH) * 100],
        ].map(([X, Y]) => `${X}% ${Y}%`);
        return `polygon(${p.join(",")})`;
      } else {
        // Approximate rounded rect with a polygon
        const steps = 48;
        const _rx = Math.min(rx || ry, w / 2),
          _ry = Math.min(ry || rx, h / 2);
        const pts = [];
        const corners = [
          { cx: x + _rx, cy: y + _ry, a0: Math.PI, a1: 1.5 * Math.PI }, // TL
          { cx: x + w - _rx, cy: y + _ry, a0: 1.5 * Math.PI, a1: 2 * Math.PI }, // TR
          { cx: x + w - _rx, cy: y + h - _ry, a0: 0, a1: 0.5 * Math.PI }, // BR
          { cx: x + _rx, cy: y + h - _ry, a0: 0.5 * Math.PI, a1: Math.PI }, // BL
        ];
        corners.forEach(({ cx, cy, a0, a1 }) => {
          for (let i = 0; i <= steps; i++) {
            const t = i / steps,
              ang = a0 + (a1 - a0) * t;
            const px = cx + _rx * Math.cos(ang);
            const py = cy + _ry * Math.sin(ang);
            pts.push([((px - vbX) / vbW) * 100, ((py - vbY) / vbH) * 100]);
          }
        });
        return `polygon(${pts.map(([X, Y]) => `${X}% ${Y}%`).join(",")})`;
      }
    } else if (tag === "circle" || tag === "ellipse") {
      const cx = +shapeNode.getAttribute("cx") || 0;
      const cy = +shapeNode.getAttribute("cy") || 0;
      const rx = tag === "circle" ? +shapeNode.getAttribute("r") || 0 : +shapeNode.getAttribute("rx") || 0;
      const ry = tag === "circle" ? rx : +shapeNode.getAttribute("ry") || 0;
      const steps = 128;
      const pts = [];
      for (let i = 0; i < steps; i++) {
        const ang = (i / steps) * 2 * Math.PI;
        const px = cx + rx * Math.cos(ang);
        const py = cy + ry * Math.sin(ang);
        pts.push([((px - vbX) / vbW) * 100, ((py - vbY) / vbH) * 100]);
      }
      return `polygon(${pts.map(([X, Y]) => `${X}% ${Y}%`).join(",")})`;
    } else {
      // Fallback: sample any shape by converting to path and sampling
      // Build a temporary SVG to use getTotalLength/getPointAtLength
      const tempSvg = document.createElementNS(SVG_NS, "svg");
      tempSvg.setAttribute("viewBox", svgRoot.getAttribute("viewBox") || `0 0 ${vbW} ${vbH}`);
      tempSvg.setAttribute("width", vbW);
      tempSvg.setAttribute("height", vbH);
      tempSvg.style.position = "absolute";
      tempSvg.style.left = "-99999px";
      document.body.appendChild(tempSvg);

      pathEl = document.createElementNS(SVG_NS, "path");
      // Try to coerce shapeNode outerHTML into a path via <use> fallback (best effort)
      // If it's not a path, we sample its bbox rectangle
      if (shapeNode.getAttribute("d")) {
        pathEl.setAttribute("d", shapeNode.getAttribute("d"));
      } else {
        const bb = shapeNode.getBBox?.() || { x: 0, y: 0, width: vbW, height: vbH };
        const d = `M ${bb.x},${bb.y} H ${bb.x + bb.width} V ${bb.y + bb.height} H ${bb.x} Z`;
        pathEl.setAttribute("d", d);
      }
      tempSvg.appendChild(pathEl);

      const len = pathEl.getTotalLength();
      const pts = [];
      const step = Math.max(1, Math.floor(len / maxPoints));
      for (let i = 0; i <= len; i += step) {
        const p = pathEl.getPointAtLength(i);
        pts.push([((p.x - vbX) / vbW) * 100, ((p.y - vbY) / vbH) * 100]);
      }
      document.body.removeChild(tempSvg);
      return `polygon(${pts.map(([X, Y]) => `${X}% ${Y}%`).join(",")})`;
    }

    // If we’re here, we have a path. Sample it to polygon.
    const tempSvg = document.createElementNS(SVG_NS, "svg");
    tempSvg.setAttribute("viewBox", svgRoot.getAttribute("viewBox") || `0 0 ${vbW} ${vbH}`);
    tempSvg.setAttribute("width", vbW);
    tempSvg.setAttribute("height", vbH);
    tempSvg.style.position = "absolute";
    tempSvg.style.left = "-99999px";
    document.body.appendChild(tempSvg);
    tempSvg.appendChild(pathEl);

    const len = pathEl.getTotalLength();
    const pts = [];
    const step = Math.max(1, Math.floor(len / maxPoints));
    for (let i = 0; i <= len; i += step) {
      const p = pathEl.getPointAtLength(i);
      pts.push([((p.x - vbX) / vbW) * 100, ((p.y - vbY) / vbH) * 100]);
    }
    document.body.removeChild(tempSvg);
    return `polygon(${pts.map(([X, Y]) => `${X}% ${Y}%`).join(",")})`;
  }

  // Main: build one, unified structure (no browser checks)
  const clippingVideo = (activeVideoSrc, targetId, insertType, poster) => {
    const widget = document.getElementById(targetId);
    if (!widget) return;

    // for video in frame, setting default poster image to 0.1 sec is trim start is at 0 sec else to the actual trim start time
    const trimStartTime = widget?.dataset?.trim?.split(",")?.[0];
    const timeStamp = !trimStartTime || trimStartTime === 0 ? VIDEO_DEFAULT_TRIM_START_TIME : trimStartTime;
    const videoSrc = activeVideoSrc?.split("#t=")?.[0];
    activeVideoSrc = `${videoSrc}#t=${timeStamp}`;

    // When replacing, update any existing video source and bail
    if (insertType === "Replace") {
      const posterImage = widget.querySelector("svg image");
      const v = widget.querySelector("video.clip-video");
      const s = v?.querySelector("source");
      if (s) {
        s.setAttribute("src", activeVideoSrc);
        if (poster) {
          v.setAttribute("poster", poster);
          posterImage.setAttribute("href", poster);
        }
      }
      return;
    }

    // Ensure there's an SVG to read shape from
    const svgRoot = widget.querySelector("svg");
    if (!svgRoot) return;

    // Get the shape node you use as the clipping outline
    const shapeNode = getNode(targetId); // must return the actual shape element
    if (!shapeNode) return;

    // Compute clip-path: polygon(...) from the shape
    const clipPoly = makePolygonFromNode(shapeNode, svgRoot, 180);

    // Prepare container & sizes
    const rect = svgRoot.getBoundingClientRect();
    if (getComputedStyle(widget).position === "static") {
      widget.style.position = "relative";
    }
    // Make sure the widget has explicit size so the absolute video can fit
    if (!widget.style.width) widget.style.width = `${Math.round(rect.width)}px`;
    if (!widget.style.height) widget.style.height = `${Math.round(rect.height)}px`;

    // Remove previous video if present
    const old = widget.querySelector("video.clip-video");
    if (old) old.remove();

    // === Build wrapper structure ===
    const outerWrapper = document.createElement("div");
    outerWrapper.className = "clip-wrapper-outer";
    outerWrapper.style.clipPath = clipPoly;
    outerWrapper.style.webkitClipPath = clipPoly;

    const innerWrapper = document.createElement("div");
    innerWrapper.className = "clip-wrapper-inner";

    // Create video (HTML element), overlay it, apply the polygon clip
    const video = document.createElement("video");
    video.className = "clip-video";
    video.setAttribute("playsinline", "");
    if (poster) video.setAttribute("poster", poster);

    // Create the <source> element
    const source = document.createElement("source");
    source.setAttribute("src", activeVideoSrc);
    source.setAttribute("type", "video/mp4");
    video.appendChild(source);

    // Ensure the shape stays in the SVG for visual reference (append if not already)
    if (!shapeNode.parentNode || shapeNode.parentNode !== svgRoot) {
      svgRoot.appendChild(shapeNode);
    }

    // Build hierarchy
    innerWrapper.appendChild(video);
    outerWrapper.appendChild(innerWrapper);
    widget.appendChild(outerWrapper);
    widget.querySelector(`svg image`).setAttribute("href", poster);
    syncVideoWithPoster(widget);

    // === Outline shape if border is applied===
    if (widget.getAttribute("data-border-style") && widget.getAttribute("data-border-style") === "solid") {
      const clone = svgRoot.cloneNode(true);
      clone.classList.add("svg-border-overlay");
      clone.querySelectorAll("image").forEach(img => img.remove());
      clone.style.position = "absolute";
      clone.style.left = "0px";
      clone.style.top = "0px";
      clone.style.overflow = "visible";
      clone.setAttribute("fill", "none");
      clone.setAttribute("stroke", widget.getAttribute("data-border-color"));
      clone.setAttribute("stroke-width", widget.getAttribute("data-border-width"));

      widget.appendChild(clone);
    }
  };

  const detachPicture = async () => {
    let activeWidgetIndex = widgets.findIndex(widget => widget.id === metadata.activeWidgetId[0]);
    let svgImage = document.querySelector(`#${metadata.activeWidgetId[0]} svg image`);
    let imageSrc = svgImage.getAttribute("href");

    // set place holder image to svg
    svgImage.setAttribute("href", widgetConfig[FRAME].placeholderImage);

    // set place holder image attributes to default
    svgImage.setAttribute("x", "0");
    svgImage.setAttribute("y", "0");
    svgImage.setAttribute("width", "512");
    svgImage.setAttribute("height", "512");

    // set width height for new image widget
    const { width: imgWidth, height: imgHeight } = await getImgDimensionFromUrl(imageSrc);
    const imgAspectRatio = imgWidth / imgHeight;
    let { width: activeBlockWidth } = document.getElementById(`${metadata.activeBlockId}`)?.getBoundingClientRect();
    activeBlockWidth = (activeBlockWidth * 100) / parseFloat(dimension.zoom);
    const width = imgWidth <= activeBlockWidth ? imgWidth : activeBlockWidth;
    const height = imgWidth <= activeBlockWidth ? imgHeight : width / imgAspectRatio;

    // set transform property for new image widget
    let widgetTransform = document.getElementById(metadata.activeWidgetId[0]).style.transform;
    let widgettranslateValue = getCssTransformObj({ transform: widgetTransform, exclude: ["rotate", "scale"] });
    let widgetTransformObj = getCssTransformObj({
      transform: widgetTransform,
      translateX: `${parseFloat(widgettranslateValue.translate.x) + 20}px`,
      translateY: `${parseFloat(widgettranslateValue.translate.y) + 20}px`,
    });
    let widgetTransformString = getCssTransformObjToString({ transformObj: widgetTransformObj });

    // create inner html for new picture widget
    // Create outer div
    const outerDiv = document.createElement("div");
    outerDiv.className = "flippable";
    outerDiv.style.height = `${height}px`;
    outerDiv.style.width = `${width}px`;
    outerDiv.style.overflow = "hidden";

    // Create inner div
    const innerDiv = document.createElement("div");
    innerDiv.className = "dhp-widget-inner";
    innerDiv.style.transformOrigin = "left top";
    innerDiv.style.transform = "translate(0px, 0px) scale(1, 1)";
    innerDiv.style.display = "block";
    innerDiv.style.opacity = "1";
    innerDiv.style.willChange = "transform";
    innerDiv.style.height = `${height}px`;
    innerDiv.style.width = `${width}px`;

    // Create img
    const img = document.createElement("img");
    img.src = imageSrc;
    img.style.height = "100%";
    img.style.width = "100%";
    img.style.display = "block";

    // Assemble the structure
    innerDiv.appendChild(img);
    outerDiv.appendChild(innerDiv);

    // create new object for new widget
    let newWidgetId = "dhp-widget-" + getRandomString(8);
    let newWidgetObj = {
      pageId: metadata.activePageId,
      blockId: metadata.activeBlockId,
      id: newWidgetId,
      classLists: ["dhp-page-widget", "dhp-root-widget"],
      duration: {
        startTime: "0.0s",
        endTime: `${pageNodes[metadata.activePageIdx].pageDuration}s`,
        totalTime: `${pageNodes[metadata.activePageIdx].pageDuration}s`,
      },
      style: {
        display: "block",
        position: "absolute",
        zIndex: 50,
        transform: widgetTransformString,
        opacity: 1,
        width: width,
        height: height,
        backfaceVisibility: "inherit",
      },
      data: widgetConfig[PICTURE].dataAttr,
      srcSet: { s3: widgets[activeWidgetIndex].data["data-src-set"] },
      innerHTML: outerDiv.outerHTML,
    };

    // update context
    let modifiedArray = JSON.parse(JSON.stringify(widgets));
    delete modifiedArray[activeWidgetIndex].data["data-src-set"];

    let newDataAttr = modifiedArray[activeWidgetIndex].data;
    let newArray;
    newArray = Object.assign([...widgets], {
      [activeWidgetIndex]: {
        ...widgets[activeWidgetIndex],
        data: {
          ...newDataAttr,
          "data-clipped": false,
        },
        innerHTML: document.getElementById(metadata.activeWidgetId[0]).innerHTML,
      },
    });
    newArray.push(newWidgetObj);
    updateWidgets(newArray);
    updateMetadata({ ...metadata, activeWidgetId: [newWidgetId], activeWidgetType: [PICTURE] });
  };

  const detachVideo = async () => {
    let activeWidgetIndex = widgets.findIndex(widget => widget.id === metadata.activeWidgetId[0]);
    let widget = document.querySelector(`#${metadata.activeWidgetId[0]}`);
    let svgImage = widget.querySelector("svg image");
    let path = getNode(metadata.activeWidgetId[0]);
    let svgVideo = document.querySelector(`#${metadata.activeWidgetId[0]} video`);
    let videoSrc = svgVideo?.querySelector("source").getAttribute("src");
    let videoPoster = svgVideo?.getAttribute("poster");

    // set place holder image to svg
    svgImage.setAttribute("href", widgetConfig[FRAME].placeholderImage);

    // set place holder image attributes to default
    svgImage.setAttribute("x", "0");
    svgImage.setAttribute("y", "0");
    svgImage.setAttribute("width", "512");
    svgImage.setAttribute("height", "512");

    // === Outline shape if border is applied===
    if (widget.getAttribute("data-border-style") && widget.getAttribute("data-border-style") === "solid") {
      const useElement = document.createElementNS("http://www.w3.org/2000/svg", "use");

      //Set required attributes
      useElement.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${path.getAttribute("id")}`);
      useElement.setAttribute("fill", "none");
      useElement.setAttribute("stroke", widget.getAttribute("data-border-color"));
      useElement.setAttribute("stroke-width", widget.getAttribute("data-border-width"));
      useElement.setAttribute("clip-path", `url(#${widget.querySelector("svg clipPath").getAttribute("id")})`);

      //Append it to the SVG
      widget.querySelector("svg").appendChild(useElement);
      document.querySelector(`#${metadata.activeWidgetId[0]} .svg-border-overlay`).remove();
    }

    document.querySelector(`#${metadata.activeWidgetId[0]} .clip-wrapper-outer`).remove();

    // set width height for new video widget
    const { width: vdoWidth, height: vdoHeight } = await getImgDimensionFromUrl(videoPoster);
    const vdoAspectRatio = vdoWidth / vdoHeight;
    let { width: activeBlockWidth } = document.getElementById(`${metadata.activeBlockId}`)?.getBoundingClientRect();
    activeBlockWidth = (activeBlockWidth * 100) / parseFloat(dimension.zoom);
    const width = vdoWidth <= activeBlockWidth ? vdoWidth : activeBlockWidth;
    const height = vdoWidth <= activeBlockWidth ? vdoHeight : width / vdoAspectRatio;

    // set transform property for new image widget
    let widgetTransform = document.getElementById(metadata.activeWidgetId[0]).style.transform;
    let widgettranslateValue = getCssTransformObj({ transform: widgetTransform, exclude: ["rotate", "scale"] });
    let widgetTransformObj = getCssTransformObj({
      transform: widgetTransform,
      translateX: `${parseFloat(widgettranslateValue.translate.x) + 20}px`,
      translateY: `${parseFloat(widgettranslateValue.translate.y) + 20}px`,
    });
    let widgetTransformString = getCssTransformObjToString({ transformObj: widgetTransformObj });

    // create inner html for new video widget
    // Create the outer div with class 'flippable'
    const outerDiv = document.createElement("div");
    outerDiv.className = "flippable";
    outerDiv.style.height = `${height}px`;
    outerDiv.style.width = `${width}px`;
    outerDiv.style.overflow = "hidden";

    // Create inner div
    const innerDiv = document.createElement("div");
    innerDiv.className = "dhp-widget-inner";
    innerDiv.style.transformOrigin = "left top";
    innerDiv.style.transform = "translate(0px, 0px) scale(1, 1)";
    innerDiv.style.display = "block";
    innerDiv.style.opacity = "1";
    innerDiv.style.willChange = "transform";
    innerDiv.style.height = `${height}px`;
    innerDiv.style.width = `${width}px`;

    // Create the video element
    const video = document.createElement("video");
    video.setAttribute("poster", videoPoster);
    video.setAttribute("src", videoSrc);
    video.setAttribute("playsinline", "true");
    video.setAttribute("disablepictureinpicture", "true");
    video.setAttribute("preload", "auto");
    video.setAttribute("loop", "loop");
    video.dataset.loop = "true";
    video.style.height = "100%";
    video.style.width = "100%";
    video.style.display = "block";
    video.style.objectFit = "cover";

    // Assemble the structure
    innerDiv.appendChild(video);
    outerDiv.appendChild(innerDiv);

    // create new object for new widget
    let newWidgetId = "dhp-widget-" + getRandomString(8);
    let newWidgetObj = {
      pageId: metadata.activePageId,
      blockId: metadata.activeBlockId,
      id: newWidgetId,
      classLists: ["dhp-page-widget", "dhp-root-widget"],
      duration: {
        startTime: "0.0s",
        endTime: `${pageNodes[metadata.activePageIdx].pageDuration}s`,
        totalTime: `${pageNodes[metadata.activePageIdx].pageDuration}s`,
      },
      style: {
        display: "block",
        position: "absolute",
        zIndex: 50,
        transform: widgetTransformString,
        opacity: 1,
        width: width,
        height: height,
        backfaceVisibility: "inherit",
      },
      data: {
        ...widgetConfig[VIDEO][STOCK].dataAttr,
        "data-loop": widgets[activeWidgetIndex].data["data-loop"],
        "data-duration": widgets[activeWidgetIndex].data["data-duration"],
        "data-trimmed-duration": widgets[activeWidgetIndex].data["data-trimmed-duration"],
        "data-trim": widgets[activeWidgetIndex].data["data-trim"],
        "data-volume": widgets[activeWidgetIndex].data["data-volume"],
      },
      innerHTML: outerDiv.outerHTML,
    };

    // update context
    let modifiedArray = JSON.parse(JSON.stringify(widgets));
    delete modifiedArray[activeWidgetIndex].data["data-loop"];
    delete modifiedArray[activeWidgetIndex].data["data-duration"];
    delete modifiedArray[activeWidgetIndex].data["data-trimmed-duration"];
    delete modifiedArray[activeWidgetIndex].data["data-playing"];
    delete modifiedArray[activeWidgetIndex].data["data-trim"];
    delete modifiedArray[activeWidgetIndex].data["data-volume"];

    let newDataAttr = modifiedArray[activeWidgetIndex].data;
    let newArray;

    newArray = Object.assign([...widgets], {
      [activeWidgetIndex]: {
        ...widgets[activeWidgetIndex],
        data: {
          ...newDataAttr,
          "data-clipped": false,
        },
        innerHTML: document.getElementById(metadata.activeWidgetId[0]).innerHTML,
      },
    });
    newArray.push(newWidgetObj);

    updateWidgets(newArray);
    updateMetadata({ ...metadata, activeWidgetId: [newWidgetId], activeWidgetType: [VIDEO] });
  };

  const syncVideoWithPoster = widget => {
    const svg = widget.querySelector("svg");
    const poster = svg?.querySelector("image");
    const outer = widget.querySelector(".clip-wrapper-outer");
    const inner = widget.querySelector(".clip-wrapper-inner");
    const video = widget.querySelector(".clip-video");
    if (!svg || !poster || !outer || !inner || !video) return;

    // 1) Ensure wrappers are correctly positioned (clip wrapper stays 100%)
    Object.assign(outer.style, {
      position: "absolute",
      left: "0px",
      top: "0px",
      width: "100%",
      height: "100%",
      overflow: "hidden",
    });
    // inner should be the containing block for the video’s left/top
    Object.assign(inner.style, {
      position: "relative",
      width: "100%",
      height: "100%",
    });

    // 2) Read the SVG viewBox (fallback if missing)
    let vbX = 0,
      vbY = 0,
      vbW = 512,
      vbH = 512;
    const vb = svg.getAttribute("viewBox");
    if (vb) {
      const parts = vb.trim().split(/\s+/).map(Number);
      if (parts.length === 4 && parts.every(n => Number.isFinite(n))) {
        [vbX, vbY, vbW, vbH] = parts;
      }
    }

    // 3) Compute scale from SVG user units -> CSS px
    const widgetW = widget.clientWidth;
    const widgetH = widget.clientHeight;
    const scaleX = widgetW / vbW;
    const scaleY = widgetH / vbH; // preserveAspectRatio="none" => non-uniform scale is OK

    // 4) Read the poster <image> box (in SVG units)
    const x = parseFloat(poster.getAttribute("x")) || 0;
    const y = parseFloat(poster.getAttribute("y")) || 0;
    const w = parseFloat(poster.getAttribute("width")) || vbW;
    const h = parseFloat(poster.getAttribute("height")) || vbH;

    // 5) Convert to CSS pixels relative to the wrapper
    const leftPx = (x - vbX) * scaleX;
    const topPx = (y - vbY) * scaleY;
    const widthPx = w * scaleX;
    const heightPx = h * scaleY;

    // 6) Apply to the video. IMPORTANT: clear previous inset/bottom/right.
    video.style.removeProperty("inset");
    video.style.right = "auto";
    video.style.bottom = "auto";

    Object.assign(video.style, {
      position: "absolute",
      left: `${leftPx}px`,
      top: `${topPx}px`,
      width: `${widthPx}px`,
      height: `${heightPx}px`,
      objectFit: "cover", // matches preserveAspectRatio="xMidYMid slice"
      pointerEvents: "none",
    });
  };

  const applyEditMode = () => {
    const widgetId = document.getElementById("highlightImage").closest(".dhp-root-widget").getAttribute("id");
    const widget = document.getElementById(widgetId);
    const targetWidgetIndex = widgets.findIndex(widget => widget.id === widgetId);
    const svg = widget.querySelector("svg");
    const path = getNode(widgetId);
    const pathId = path.id;
    const originalClippathId = svg.querySelector("clipPath").getAttribute("id");
    const defs = svg.querySelector("defs");

    // for image
    if (widget.getAttribute("data-clipped") === "image") {
      if (defs) defs.remove();
      svg.querySelectorAll("use").forEach(el => el.remove());
      svg.querySelector("#highlightImage").remove();

      // base image attrs and styles modification
      svg.querySelector("#baseImage").setAttribute("clip-path", `url(#${originalClippathId})`);
      svg.querySelector("#baseImage").style.opacity = 1;
      svg.querySelector("#baseImage").removeAttribute("id");

      // Create <clipPath> structure
      const clipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
      clipPath.setAttribute("id", originalClippathId);
      const useEl = document.createElementNS("http://www.w3.org/2000/svg", "use");
      useEl.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${pathId}`);
      clipPath.appendChild(useEl);
      svg.appendChild(clipPath);

      // === Outline shape if border is applied===
      if (widget.getAttribute("data-border-style") && widget.getAttribute("data-border-style") === "solid") {
        const outline = document.createElementNS("http://www.w3.org/2000/svg", "use");
        outline.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${pathId}`);
        outline.setAttribute("fill", "none");
        outline.setAttribute("stroke", widget.getAttribute("data-border-color"));
        outline.setAttribute("stroke-width", widget.getAttribute("data-border-width"));
        outline.setAttribute("clip-path", `url(#${originalClippathId})`);
        svg.appendChild(outline);
      }
    }
    // for video
    else {
      if (defs) defs.remove();
      svg.querySelectorAll("use").forEach(el => el.remove());
      svg.querySelector("#highlightImage").remove();

      // base image attrs and styles modification
      svg.querySelector("#baseImage").setAttribute("clip-path", `url(#${originalClippathId})`);
      svg.querySelector("#baseImage").style.opacity = 1;
      svg.querySelector("#baseImage").removeAttribute("id");

      // Create <clipPath> structure
      const clipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
      clipPath.setAttribute("id", originalClippathId);
      const useEl = document.createElementNS("http://www.w3.org/2000/svg", "use");
      useEl.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${pathId}`);
      clipPath.appendChild(useEl);
      svg.appendChild(clipPath);

      widget.querySelector(".clip-wrapper-outer").style.display = "block";
      syncVideoWithPoster(widget);
    }

    // update context
    const widgetObject = widgets[targetWidgetIndex];
    let newArray;
    newArray = Object.assign([...widgets], {
      [targetWidgetIndex]: {
        ...widgetObject,
        innerHTML: document.getElementById(widgetId).innerHTML,
      },
    });
    updateWidgets(newArray);
  };

  const revertEditMode = () => {
    const widgetId = document.getElementById("highlightImage")?.closest(".dhp-root-widget").getAttribute("id");
    const targetWidgetIndex = widgets.findIndex(widget => widget.id === widgetId);

    document.getElementById(widgetId).innerHTML = widgets[targetWidgetIndex]?.innerHTML;
  };

  const detectOutsideClick = e => {
    if (isFrameContentActive) {
      if (e.target?.getAttribute("id") === "highlightImage" || e.target?.classList.contains("frame-action-button"))
        return;
      else {
        const handler = document.getElementById("dhp-widget-handler");

        handler.classList.add("d-none");
        handler.style.display = "none";
        handler.style.opacity = 0;

        setIsFrameContentActive(false);
        revertEditMode();
      }
    }
  };

  useEffect(() => {
    if (
      metadata.activeWidgetId.length === 0 ||
      metadata.activeWidgetId.length > 1 ||
      metadata.activeWidgetType[0] !== FRAME ||
      (metadata.activeWidgetType[0] === FRAME &&
        !["image", "video"].includes(document.getElementById(metadata.activeWidgetId[0])?.getAttribute("data-clipped")))
    )
      return; // return if multi widget, not frame widget, if frame is blank(no pucture or video inside)

    const widget = document.getElementById(metadata.activeWidgetId[0]);
    const svg = widget.querySelector("svg");
    const originalPath = getNode(widget.getAttribute("id"));
    const originalPathId = originalPath.getAttribute("id");
    const originalClippathId = svg.querySelector("clipPath").getAttribute("id");
    let originalImage =
      document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-clipped") === "image"
        ? svg.querySelector("image")
        : widget.querySelector(".svg-border-overlay")
        ? widget.querySelector(".svg-border-overlay")
        : widget.querySelector(".clip-wrapper-outer");

    if (!svg || !originalPath || !originalImage) return;

    const addResizeHandle = () => {
      const widget = document.getElementById("highlightImage").closest(".dhp-root-widget");
      const svg = widget.querySelector("svg");
      const fullImage = svg?.querySelector("image:not([mask])"); // faded image without mask

      if (!fullImage) return;

      const bbox = fullImage.getBoundingClientRect(); // Get the image's bounding box relative to viewport
      const parentRect = widget.offsetParent.getBoundingClientRect(); // Get the parent container's offset to make handler relative
      const activeWidgetTransform = widget.style.transform;
      let widgetTransformObj = getCssTransformObj({
        transform: activeWidgetTransform,
        translateX: `${bbox.left - parentRect.left}px`,
        translateY: `${bbox.top - parentRect.top}px`,
      });
      let widgetTransformString = getCssTransformObjToString({ transformObj: widgetTransformObj });

      setTimeout(() => {
        const handler = document.getElementById("dhp-widget-handler");
        const editorOuterWrapTop = document.querySelectorAll(".dhp-page-canvas")[0].getBoundingClientRect().top;
        const activeBlockTop = document.getElementById(metadata.activeBlockId).getBoundingClientRect().top;
        const addedTop = WIDGET_HANDLER_MARGIN_TOP + GENERIC_EDITOR_WRAP_PADDING;
        const activePageTop = isTimeLineViewOpen ? 0 : Math.abs(activeBlockTop - editorOuterWrapTop);
        const top = `${
          activePageTop +
          ([TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType)
            ? getZoomedValue(addedTop, dimension.zoom)
            : addedTop)
        }px`;

        handler.classList.remove("d-none");

        // Position the handler exactly over the full image
        handler.style.position = "absolute";
        handler.style.display = "block";
        handler.style.opacity = 1;
        handler.style.width = bbox.width + "px";
        handler.style.height = bbox.height + "px";
        handler.style.transform = widgetTransformString;
        handler.style.left = 0;
        handler.style.top = top;
      }, 100);
    };

    const handleEditMode = () => {
      // for video type frame hide outside video wrapper
      if (widget.querySelector(".clip-wrapper-outer")) {
        widget.querySelector(".clip-wrapper-outer").style.display = "none";
        originalImage = svg.querySelector("image");
      }

      setIsFrameContentActive(true);
      updateMetadata({ ...metadata, activeWidgetId: false, activeWidgetType: false });

      // Extract href, video url and size from original image/video
      const imageHref = originalImage.getAttribute("href");
      const imageX = originalImage.getAttribute("x") || 0;
      const imageY = originalImage.getAttribute("y") || 0;
      const imageWidth = originalImage.getAttribute("width") || 900;
      const imageHeight = originalImage.getAttribute("height") || 700;
      const videoUrl = widget?.querySelector("video source")?.getAttribute("src");

      // Clear all children (optional: clone shape first)
      while (svg.firstChild) svg.removeChild(svg.firstChild);

      // === <defs> with clipPath & mask ===
      const defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");

      const clipPath = document.createElementNS("http://www.w3.org/2000/svg", "clipPath");
      clipPath.setAttribute("id", originalClippathId);
      const clipUse = document.createElementNS("http://www.w3.org/2000/svg", "use");
      clipUse.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${originalPathId}`);
      clipPath.appendChild(clipUse);
      defs.appendChild(clipPath);

      const mask = document.createElementNS("http://www.w3.org/2000/svg", "mask");
      mask.setAttribute("id", "highlightMask");

      const rect = document.createElementNS("http://www.w3.org/2000/svg", "rect");
      rect.setAttribute("width", "100%");
      rect.setAttribute("height", "100%");
      rect.setAttribute("fill", "black");
      mask.appendChild(rect);

      const useWhite = document.createElementNS("http://www.w3.org/2000/svg", "use");
      useWhite.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${originalPathId}`);
      useWhite.setAttribute("fill", "white");
      mask.appendChild(useWhite);

      defs.appendChild(mask);
      svg.appendChild(defs);

      // === Append shapePath again ===
      svg.appendChild(originalPath);

      // === Background faded image ===
      const fadedImg = document.createElementNS("http://www.w3.org/2000/svg", "image");
      fadedImg.setAttribute("href", imageHref);
      fadedImg.setAttribute("x", imageX);
      fadedImg.setAttribute("y", imageY);
      fadedImg.setAttribute("width", imageWidth);
      fadedImg.setAttribute("height", imageHeight);
      fadedImg.setAttribute("preserveAspectRatio", "xMidYMid slice");
      fadedImg.setAttribute("id", "baseImage");
      fadedImg.style.opacity = "0.2";

      if (document.getElementById(metadata.activeWidgetId[0]).getAttribute("data-clipped") === "video") {
        fadedImg.setAttribute("data-video-url", videoUrl);
      }
      svg.appendChild(fadedImg);

      // === Foreground image masked ===
      const clippedImg = document.createElementNS("http://www.w3.org/2000/svg", "image");
      clippedImg.setAttribute("href", imageHref);
      clippedImg.setAttribute("x", imageX);
      clippedImg.setAttribute("y", imageY);
      clippedImg.setAttribute("width", imageWidth);
      clippedImg.setAttribute("height", imageHeight);
      clippedImg.setAttribute("preserveAspectRatio", "xMidYMid slice");
      clippedImg.setAttribute("mask", "url(#highlightMask)");
      clippedImg.setAttribute("id", "highlightImage");
      clippedImg.style.opacity = "1";
      svg.appendChild(clippedImg);

      // === Outline shape ===
      const outline = document.createElementNS("http://www.w3.org/2000/svg", "use");
      outline.setAttributeNS("http://www.w3.org/1999/xlink", "xlink:href", `#${originalPathId}`);
      outline.setAttribute("fill", "none");
      // if border applied
      if (widget.getAttribute("data-border-style") && widget.getAttribute("data-border-style") === "solid") {
        outline.setAttribute("stroke", widget.getAttribute("data-border-color"));
        outline.setAttribute("stroke-width", widget.getAttribute("data-border-width"));
        outline.setAttribute("clip-path", `url(#${originalClippathId})`);
      }
      svg.appendChild(outline);

      // show resize handle
      addResizeHandle();
    };

    originalImage.addEventListener("dblclick", handleEditMode);

    return () => {
      originalImage.removeEventListener("dblclick", handleEditMode);
    };
  }, [metadata.activeWidgetId, widgets]);

  // useEffect(() => {
  //   if (!isFrameContentActive) return;

  //   const widget = document.getElementById("highlightImage").closest(".dhp-root-widget");
  //   const svg = widget.querySelector("svg");
  //   const originalPath = getNode(widget.getAttribute("id"));
  //   let baseImg = svg.querySelector("#baseImage");
  //   let highlightImg = svg.querySelector("#highlightImage");
  //   const originalImage = highlightImg;

  //   svg.style.overflow = "visible"; // make image overflow

  //   if (!svg || !originalPath || !originalImage) return;

  //   const clipBounds = originalPath.getBBox();
  //   const handler = document.getElementById("dhp-widget-handler");
  //   const resizers = handler?.querySelectorAll(".resizer2");
  //   const resizeHandlers = [];

  //   let isDragging = false;
  //   let isResizing = false;
  //   let dragOffset = { x: 0, y: 0 };
  //   let startX, startY;
  //   let currentResizeDir = "";

  //   const startDrag = e => {
  //     isDragging = true;
  //     startX = e.clientX;
  //     startY = e.clientY;
  //     dragOffset.x = parseFloat(originalImage.getAttribute("x")) || 0;
  //     dragOffset.y = parseFloat(originalImage.getAttribute("y")) || 0;
  //     e.preventDefault();
  //   };

  //   const onMouseMove = e => {
  //     if (isDragging === true) {
  //       const dx = e.clientX - startX;
  //       const dy = e.clientY - startY;
  //       let newX = dragOffset.x + dx;
  //       let newY = dragOffset.y + dy;

  //       const imgWidth = parseFloat(baseImg.getAttribute("width"));
  //       const imgHeight = parseFloat(baseImg.getAttribute("height"));

  //       newX = Math.max(clipBounds.x + clipBounds.width - imgWidth, Math.min(newX, clipBounds.x));
  //       newY = Math.max(clipBounds.y + clipBounds.height - imgHeight, Math.min(newY, clipBounds.y));

  //       const prevX = parseFloat(highlightImg.getAttribute("x")) || 0;
  //       const prevY = parseFloat(highlightImg.getAttribute("y")) || 0;

  //       baseImg?.setAttribute("x", newX);
  //       baseImg?.setAttribute("y", newY);
  //       highlightImg?.setAttribute("x", newX);
  //       highlightImg?.setAttribute("y", newY);

  //       // For safari
  //       if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
  //         highlightImg = nudgeSafari(highlightImg, "drag");
  //         baseImg = nudgeSafari(baseImg, "drag");
  //       }

  //       // update handler
  //       updateHandler();
  //     }

  //     if (isResizing) {
  //       const dx = e.clientX - startX;
  //       const dy = e.clientY - startY;

  //       const originalX = dragOffset.x;
  //       const originalY = dragOffset.y;
  //       let newWidth = dragOffset.width;
  //       let newHeight = dragOffset.height;
  //       let newX = originalX;
  //       let newY = originalY;

  //       if (currentResizeDir === "se") {
  //         newWidth = dragOffset.width + dx;
  //         newHeight = dragOffset.height + dy;
  //       } else if (currentResizeDir === "sw") {
  //         newWidth = dragOffset.width - dx;
  //         newHeight = dragOffset.height + dy;
  //         newX = originalX + dx;
  //       } else if (currentResizeDir === "ne") {
  //         newWidth = dragOffset.width + dx;
  //         newHeight = dragOffset.height - dy;
  //         newY = originalY + dy;
  //       } else if (currentResizeDir === "nw") {
  //         newWidth = dragOffset.width - dx;
  //         newHeight = dragOffset.height - dy;
  //         newX = originalX + dx;
  //         newY = originalY + dy;
  //       }

  //       // 🔒 Get clipPath bounds
  //       const clipLeft = clipBounds.x;
  //       const clipTop = clipBounds.y;
  //       const clipRight = clipBounds.x + clipBounds.width;
  //       const clipBottom = clipBounds.y + clipBounds.height;

  //       // 🔒 Enforce: image must always cover clip bounds
  //       const minRightWidth = clipRight - newX;
  //       const minBottomHeight = clipBottom - newY;

  //       if (newWidth < minRightWidth) {
  //         newWidth = minRightWidth;
  //       }

  //       if (newHeight < minBottomHeight) {
  //         newHeight = minBottomHeight;
  //       }

  //       // 🔒 Adjust left edge if moving inward past clip
  //       if ((currentResizeDir === "nw" || currentResizeDir === "sw") && newX > clipLeft) {
  //         const delta = newX - clipLeft;
  //         newX = clipLeft;
  //         newWidth += delta;
  //       }

  //       // 🔒 Adjust top edge if moving inward past clip
  //       if ((currentResizeDir === "nw" || currentResizeDir === "ne") && newY > clipTop) {
  //         const delta = newY - clipTop;
  //         newY = clipTop;
  //         newHeight += delta;
  //       }

  //       // ✅ Set final values
  //       baseImg.setAttribute("x", newX);
  //       baseImg.setAttribute("y", newY);
  //       baseImg.setAttribute("width", newWidth);
  //       baseImg.setAttribute("height", newHeight);

  //       highlightImg.setAttribute("x", newX);
  //       highlightImg.setAttribute("y", newY);
  //       highlightImg.setAttribute("width", newWidth);
  //       highlightImg.setAttribute("height", newHeight);

  //       // For safari
  //       if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
  //         nudgeSafari(highlightImg, "resize");
  //       }

  //       // update handler
  //       updateHandler();
  //     }
  //   };

  //   const onMouseUp = () => {
  //     isDragging = false;
  //     isResizing = false;
  //     currentResizeDir = "";
  //   };

  //   const updateHandler = () => {
  //     const handler = document.getElementById("dhp-widget-handler");
  //     const image = widget.querySelector("#highlightImage");

  //     if (!handler || !image || !widget) return;

  //     // Step 1: Get widget and page scale
  //     const block = widget.closest(".dhp-page-block");
  //     const blockScale = parseFloat(block.style.transform.match(/scale\(([^)]+)\)/)?.[1]) || 1;

  //     // Step 2: Get SVG image properties (x, y, width, height)
  //     const imgX = parseFloat(image.getAttribute("x") || 0);
  //     const imgY = parseFloat(image.getAttribute("y") || 0);
  //     const imgWidth = parseFloat(image.getAttribute("width") || 0);
  //     const imgHeight = parseFloat(image.getAttribute("height") || 0);

  //     // Step 3: Calculate real image position relative to .dhp-page-block
  //     const widgetBox = widget.getBoundingClientRect();
  //     const blockBox = block.getBoundingClientRect();

  //     // Image position inside the widget + widget position inside page-block
  //     const left = (widgetBox.left - blockBox.left + imgX * (widgetBox.width / 512)) / blockScale;
  //     const top = (widgetBox.top - blockBox.top + imgY * (widgetBox.height / 512)) / blockScale;

  //     // Step 4: Scale width/height
  //     const scaleRatioX = widgetBox.width / 512;
  //     const scaleRatioY = widgetBox.height / 512;

  //     const width = (imgWidth * scaleRatioX) / blockScale;
  //     const height = (imgHeight * scaleRatioY) / blockScale;

  //     const activeWidgetTransform = widget.style.transform;
  //     let widgetTransformObj = getCssTransformObj({
  //       transform: activeWidgetTransform,
  //       translateX: `${left * (dimension.zoom / 100)}px`,
  //       translateY: `${top * (dimension.zoom / 100)}px`,
  //     });
  //     let widgetTransformString = getCssTransformObjToString({ transformObj: widgetTransformObj });

  //     // Step 5: Apply to handler
  //     handler.style.width = `${width * (dimension.zoom / 100)}px`;
  //     handler.style.height = `${height * (dimension.zoom / 100)}px`;
  //     handler.style.transform = widgetTransformString;
  //   };

  //   const nudgeSafari = (img, event) => {
  //     // during resize
  //     if (event === "resize") {
  //       const x = parseFloat(img.getAttribute("x")) || 0;
  //       img.setAttribute("x", x + 0.01); // tiny nudge
  //       requestAnimationFrame(() => {
  //         img.setAttribute("x", x);
  //       });
  //     }
  //     // during drag
  //     else {
  //       if (!img || !img.parentNode) return img; // safety check

  //       const x = img.getAttribute("x");
  //       const y = img.getAttribute("y");

  //       // Clone and replace
  //       const clone = img.cloneNode(true);
  //       img.parentNode.replaceChild(clone, img);

  //       if (x !== null) clone.setAttribute("x", x);
  //       if (y !== null) clone.setAttribute("y", y);

  //       return clone; // new reference
  //     }
  //   };

  //   const onSvgMouseDown = e => {
  //     if (e.target && e.target.id === "highlightImage") {
  //       startDrag(e);
  //     }
  //   };

  //   // === Attach drag listener to image ===
  //   svg.addEventListener("mousedown", onSvgMouseDown); // originalImage.addEventListener("mousedown", startDrag); // it was previous but not working in safari

  //   // === Attach resize listeners ===
  //   resizers?.forEach(resizer => {
  //     const handler = e => {
  //       e.stopPropagation();
  //       e.preventDefault(); // prevent selection flicker

  //       isResizing = true;
  //       isDragging = false;

  //       startX = e.clientX;
  //       startY = e.clientY;

  //       // 🔁 Get fresh values from image
  //       dragOffset.x = parseFloat(baseImg.getAttribute("x")) || 0;
  //       dragOffset.y = parseFloat(baseImg.getAttribute("y")) || 0;
  //       dragOffset.width = parseFloat(baseImg.getAttribute("width")) || 0;
  //       dragOffset.height = parseFloat(baseImg.getAttribute("height")) || 0;

  //       // Determine direction
  //       if (resizer.classList.contains("nw")) currentResizeDir = "nw";
  //       else if (resizer.classList.contains("ne")) currentResizeDir = "ne";
  //       else if (resizer.classList.contains("sw")) currentResizeDir = "sw";
  //       else currentResizeDir = "se";
  //     };

  //     resizer.addEventListener("mousedown", handler);
  //     resizeHandlers.push({ resizer, handler }); // Save for cleanup
  //   });

  //   // === Global mouse events ===
  //   document.addEventListener("mousemove", onMouseMove);
  //   document.addEventListener("mouseup", onMouseUp);

  //   // === Cleanup ===
  //   return () => {
  //     svg.removeEventListener("mousedown", onSvgMouseDown); // originalImage.removeEventListener("mousedown", startDrag); // it was previous but not working in safari
  //     resizeHandlers.forEach(({ resizer, handler }) => {
  //       resizer.removeEventListener("mousedown", handler);
  //     });
  //     document.removeEventListener("mousemove", onMouseMove);
  //     document.removeEventListener("mouseup", onMouseUp);
  //   };
  // }, [isFrameContentActive]);

  useEffect(() => {
    if (!isFrameContentActive) return;

    const widget = document.getElementById("highlightImage").closest(".dhp-root-widget");
    const svg = widget.querySelector("svg");
    const originalPath = getNode(widget.getAttribute("id"));
    let baseImg = svg.querySelector("#baseImage");
    let highlightImg = svg.querySelector("#highlightImage");

    if (!svg || !originalPath || !highlightImg) return;

    svg.style.overflow = "visible"; // allow overflow

    const clipBounds = originalPath.getBBox();
    const handler = document.getElementById("dhp-widget-handler");
    const resizers = handler?.querySelectorAll(".resizer2");
    const resizeHandlers = [];

    let isDragging = false;
    let isResizing = false;
    let dragOffset = { x: 0, y: 0, width: 0, height: 0 };
    let startX, startY;
    let currentResizeDir = "";

    // ✅ Safari-safe nudge + clone
    const nudgeSafari = (img, event) => {
      if (!img || !img.parentNode) return img;

      // Clone node for Safari repaint
      const clone = img.cloneNode(true);
      img.parentNode.replaceChild(clone, img);

      // Keep values
      const x = parseFloat(img.getAttribute("x")) || 0;
      const y = parseFloat(img.getAttribute("y")) || 0;
      const width = parseFloat(img.getAttribute("width")) || 0;
      const height = parseFloat(img.getAttribute("height")) || 0;

      clone.setAttribute("x", x);
      clone.setAttribute("y", y);
      clone.setAttribute("width", width);
      clone.setAttribute("height", height);

      return clone; // return new reference
    };

    const startDrag = e => {
      isDragging = true;
      startX = e.clientX;
      startY = e.clientY;
      dragOffset.x = parseFloat(highlightImg.getAttribute("x")) || 0;
      dragOffset.y = parseFloat(highlightImg.getAttribute("y")) || 0;
      e.preventDefault();
    };

    const onMouseMove = e => {
      if (isDragging) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;
        let newX = dragOffset.x + dx;
        let newY = dragOffset.y + dy;

        const imgWidth = parseFloat(highlightImg.getAttribute("width"));
        const imgHeight = parseFloat(highlightImg.getAttribute("height"));

        // Keep inside clip bounds
        newX = Math.max(clipBounds.x + clipBounds.width - imgWidth, Math.min(newX, clipBounds.x));
        newY = Math.max(clipBounds.y + clipBounds.height - imgHeight, Math.min(newY, clipBounds.y));

        highlightImg.setAttribute("x", newX);
        highlightImg.setAttribute("y", newY);
        baseImg.setAttribute("x", newX);
        baseImg.setAttribute("y", newY);

        // Safari repaint
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
          highlightImg = nudgeSafari(highlightImg, "drag");
          baseImg = nudgeSafari(baseImg, "drag");
        }

        updateHandler();
      }

      if (isResizing) {
        const dx = e.clientX - startX;
        const dy = e.clientY - startY;

        let newX = dragOffset.x;
        let newY = dragOffset.y;
        let newWidth = dragOffset.width;
        let newHeight = dragOffset.height;

        if (currentResizeDir === "se") {
          newWidth += dx;
          newHeight += dy;
        } else if (currentResizeDir === "sw") {
          newWidth -= dx;
          newHeight += dy;
          newX += dx;
        } else if (currentResizeDir === "ne") {
          newWidth += dx;
          newHeight -= dy;
          newY += dy;
        } else if (currentResizeDir === "nw") {
          newWidth -= dx;
          newHeight -= dy;
          newX += dx;
          newY += dy;
        }

        // Enforce clip bounds
        const clipLeft = clipBounds.x;
        const clipTop = clipBounds.y;
        const clipRight = clipBounds.x + clipBounds.width;
        const clipBottom = clipBounds.y + clipBounds.height;

        if (newWidth < clipRight - newX) newWidth = clipRight - newX;
        if (newHeight < clipBottom - newY) newHeight = clipBottom - newY;

        if ((currentResizeDir === "nw" || currentResizeDir === "sw") && newX > clipLeft) {
          const delta = newX - clipLeft;
          newX = clipLeft;
          newWidth += delta;
        }
        if ((currentResizeDir === "nw" || currentResizeDir === "ne") && newY > clipTop) {
          const delta = newY - clipTop;
          newY = clipTop;
          newHeight += delta;
        }

        highlightImg.setAttribute("x", newX);
        highlightImg.setAttribute("y", newY);
        highlightImg.setAttribute("width", newWidth);
        highlightImg.setAttribute("height", newHeight);

        baseImg.setAttribute("x", newX);
        baseImg.setAttribute("y", newY);
        baseImg.setAttribute("width", newWidth);
        baseImg.setAttribute("height", newHeight);

        // Safari repaint on resize
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
          highlightImg = nudgeSafari(highlightImg, "resize");
          baseImg = nudgeSafari(baseImg, "resize");
        }

        updateHandler();
      }
    };

    const onMouseUp = () => {
      isDragging = false;
      isResizing = false;
      currentResizeDir = "";
    };

    const updateHandler = () => {
      const handler = document.getElementById("dhp-widget-handler");
      const image = highlightImg;

      if (!handler || !image || !widget) return;

      const block = widget.closest(".dhp-page-block");
      const blockScale = parseFloat(block.style.transform.match(/scale\(([^)]+)\)/)?.[1]) || 1;

      const imgX = parseFloat(image.getAttribute("x") || 0);
      const imgY = parseFloat(image.getAttribute("y") || 0);
      const imgWidth = parseFloat(image.getAttribute("width") || 0);
      const imgHeight = parseFloat(image.getAttribute("height") || 0);

      const widgetBox = widget.getBoundingClientRect();
      const blockBox = block.getBoundingClientRect();

      const left = (widgetBox.left - blockBox.left + imgX * (widgetBox.width / 512)) / blockScale;
      const top = (widgetBox.top - blockBox.top + imgY * (widgetBox.height / 512)) / blockScale;

      const scaleRatioX = widgetBox.width / 512;
      const scaleRatioY = widgetBox.height / 512;

      const width = (imgWidth * scaleRatioX) / blockScale;
      const height = (imgHeight * scaleRatioY) / blockScale;

      const activeWidgetTransform = widget.style.transform;
      const zoomValue = [TYPE_INFOGRAPHIC, TYPE_PROPOSAL].includes(documentType) ? 100 : dimension.zoom;
      let widgetTransformObj = getCssTransformObj({
        transform: activeWidgetTransform,
        translateX: `${left * (zoomValue / 100)}px`,
        translateY: `${top * (zoomValue / 100)}px`,
      });
      let widgetTransformString = getCssTransformObjToString({ transformObj: widgetTransformObj });

      // Step 5: Apply to handler
      handler.style.width = `${width * (zoomValue / 100)}px`;
      handler.style.height = `${height * (zoomValue / 100)}px`;
      handler.style.transform = widgetTransformString;
    };

    const onSvgMouseDown = e => {
      if (e.target && e.target.id === "highlightImage") startDrag(e);
    };

    // Attach drag listener
    svg.addEventListener("mousedown", onSvgMouseDown);

    // Attach resize listeners
    resizers?.forEach(resizer => {
      const handler = e => {
        e.stopPropagation();
        e.preventDefault();

        isResizing = true;
        isDragging = false;

        startX = e.clientX;
        startY = e.clientY;

        dragOffset.x = parseFloat(highlightImg.getAttribute("x")) || 0;
        dragOffset.y = parseFloat(highlightImg.getAttribute("y")) || 0;
        dragOffset.width = parseFloat(highlightImg.getAttribute("width")) || 0;
        dragOffset.height = parseFloat(highlightImg.getAttribute("height")) || 0;

        if (resizer.classList.contains("nw")) currentResizeDir = "nw";
        else if (resizer.classList.contains("ne")) currentResizeDir = "ne";
        else if (resizer.classList.contains("sw")) currentResizeDir = "sw";
        else currentResizeDir = "se";

        // Safari clone once at start of resize
        if (/^((?!chrome|android).)*safari/i.test(navigator.userAgent)) {
          highlightImg = nudgeSafari(highlightImg, "resize");
          baseImg = nudgeSafari(baseImg, "resize");
        }
      };
      resizer.addEventListener("mousedown", handler);
      resizeHandlers.push({ resizer, handler });
    });

    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);

    return () => {
      svg.removeEventListener("mousedown", onSvgMouseDown);
      resizeHandlers.forEach(({ resizer, handler }) => {
        resizer.removeEventListener("mousedown", handler);
      });
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
    };
  }, [isFrameContentActive]);

  useEffect(() => {
    document.addEventListener("mousedown", detectOutsideClick);

    // === Cleanup ===
    return () => {
      document.removeEventListener("mousedown", detectOutsideClick);
    };
  });

  return { clippingPicture, clippingVideo, applyEditMode, revertEditMode, detachPicture, detachVideo };
};

export default useClippingPicture;
